import { useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Clock3, Copy, QrCode, RefreshCw, Trash2 } from "lucide-react";
import type { ShortLink } from "../../shared/types";
import { api, jsonBody } from "../lib/api";
import { accountRoleLabel, date, number, relative, statusLabels } from "../lib/format";
import { Button, Dialog, Empty, Tag, useToast } from "./ui";
import { QrDialog } from "./qr-dialog";

function tone(status: ShortLink["status"]): "accent" | "neutral" | "outline" | "dark" {
  if (status === "pending" || status === "grace") return "accent";
  if (status === "released" || status === "disabled" || status === "rejected") return "outline";
  return "neutral";
}

export function LinkTable({ links, compact = false }: { links: ShortLink[]; compact?: boolean }) {
  const queryClient = useQueryClient();
  const toast = useToast();
  const [qr, setQr] = useState<ShortLink | null>(null);
  const [releaseCandidate, setReleaseCandidate] = useState<ShortLink | null>(null);
  const [renewCutoff] = useState(() => Date.now() + 7 * 86_400_000);
  const renewMutation = useMutation({
    mutationFn: (link: ShortLink) =>
      api<ShortLink>(`/api/links/${link.id}/renew`, { method: "POST", ...jsonBody({ mode: "default" }) }),
    onSuccess: async () => {
      toast.push("短链已续期");
      await queryClient.invalidateQueries();
    },
    onError: toast.fromError,
  });
  const releaseMutation = useMutation({
    mutationFn: (link: ShortLink) => api<ShortLink>(`/api/links/${link.id}`, { method: "DELETE" }),
    onSuccess: async () => {
      setReleaseCandidate(null);
      toast.push("短链已释放，目标地址已清除");
      await queryClient.invalidateQueries();
    },
    onError: toast.fromError,
  });

  const copy = async (link: ShortLink) => {
    try {
      await navigator.clipboard.writeText(link.shortUrl);
      toast.push("短链已复制");
    } catch (error) {
      toast.fromError(error);
    }
  };

  if (links.length === 0) {
    return <Empty title="这里还没有短链" body="创建第一条短链后，它的状态、点击和到期时间会出现在这里。" />;
  }
  return (
    <>
      <div className="table-wrap">
        <table className={`data-table ${compact ? "data-table--compact" : ""}`}>
          <thead>
            <tr>
              <th>短链 · SLUG</th>
              <th>目标 · TARGET</th>
              {!compact ? <th>归属 · OWNER</th> : null}
              <th className="numeric">点击</th>
              <th>到期 · EXPIRY</th>
              <th>状态</th>
              <th aria-label="操作" />
            </tr>
          </thead>
          <tbody>
            {links.map((link) => (
              <tr key={link.id}>
                <td data-label="短链">
                  <a className="slug-link" href={link.shortUrl} target="_blank" rel="noreferrer">/{link.slug}</a>
                  <span className="mobile-subline">{link.shortUrl}</span>
                </td>
                <td data-label="目标"><span className="truncate target-cell" title={link.targetUrl}>{link.targetUrl || "—— 已释放"}</span></td>
                {!compact ? (
                  <td data-label="归属">
                    {link.owner ? <><Tag tone={link.owner.role === "user" && link.owner.tier === "vip" ? "vip" : link.owner.role === "user" ? "outline" : "neutral"}>{accountRoleLabel(link.owner.role, link.owner.tier)}</Tag><span className="owner-email">{link.owner.email}</span></> : "—"}
                  </td>
                ) : null}
                <td className="numeric" data-label="点击">{number(link.clickCount)}</td>
                <td data-label="到期"><strong>{relative(link.expiresAt)}</strong><span className="cell-note">{date(link.expiresAt)}</span></td>
                <td data-label="状态"><Tag tone={tone(link.status)}>{statusLabels[link.status]}</Tag></td>
                <td className="row-actions" data-label="操作">
                  <button className="icon-button" type="button" onClick={() => void copy(link)} title="复制短链"><Copy size={14} /></button>
                  <button className="icon-button" type="button" onClick={() => setQr(link)} title="二维码" disabled={link.status === "released"}><QrCode size={14} /></button>
                  {(link.status === "grace" || (link.expiresAt && new Date(link.expiresAt).getTime() < renewCutoff)) ? (
                    <button className="icon-button" type="button" onClick={() => renewMutation.mutate(link)} title="续期"><RefreshCw size={14} /></button>
                  ) : null}
                  {!compact && link.status !== "released" ? (
                    <button className="icon-button icon-button--danger" type="button" onClick={() => setReleaseCandidate(link)} title="释放"><Trash2 size={14} /></button>
                  ) : null}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <QrDialog link={qr} onClose={() => setQr(null)} />
      <Dialog open={Boolean(releaseCandidate)} onClose={() => setReleaseCandidate(null)} title="释放短链" eyebrow="DESTRUCTIVE · RELEASE">
        {releaseCandidate ? (
          <div className="confirm-block">
            <Clock3 size={28} />
            <p><strong>{releaseCandidate.shortUrl}</strong> 将立即停止跳转，目标地址会从当前记录中清除。后缀经过冷却期后可被重新分配。</p>
            <div className="dialog-actions">
              <Button onClick={() => setReleaseCandidate(null)}>取消</Button>
              <Button variant="danger" onClick={() => releaseMutation.mutate(releaseCandidate)} disabled={releaseMutation.isPending}>
                <Trash2 size={15} />确认释放
              </Button>
            </div>
          </div>
        ) : null}
      </Dialog>
    </>
  );
}
